import { Link } from 'react-router-dom'
import { BookOpen } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { teacherMeClassesQueryOptions } from '@/features/academics/hooks/queries/useTeacherDashboardData'
import { useTeacherSchoolScope } from '@/features/dashboard/hooks/useTeacherSchoolScope'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

const classHref = (classId: string) =>
  `/academics/class/${classId}?view=${encodeURIComponent('attendance')}`

/**
 * Teacher-only sidebar shortcuts to current classes (same data as teacher dashboard).
 */
export function TeacherSidebarClasses() {
  const { schoolId } = useTeacherSchoolScope()
  const { data: classes, isLoading } = useQuery({
    ...teacherMeClassesQueryOptions(schoolId!),
    enabled: !!schoolId,
  })

  if (!schoolId) {
    return (
      <div className='rounded-lg border border-border/60 bg-muted/10 px-3 py-2 text-muted-foreground text-xs'>
        Select a school on the dashboard to list your classes.
      </div>
    )
  }

  return (
    <div className='space-y-2'>
      <div className='px-3 font-semibold text-muted-foreground text-xs uppercase tracking-widest'>
        My classes
      </div>
      <nav className='space-y-1' aria-label='My classes'>
        {isLoading ? (
          <div className='space-y-2 px-1'>
            {[0, 1, 2].map(i => (
              <Skeleton key={i} className='h-9 w-full rounded-md' />
            ))}
          </div>
        ) : !classes?.length ? (
          <p className='px-3 text-muted-foreground text-xs'>No classes assigned yet.</p>
        ) : (
          classes.map(cls => (
            <Link
              key={cls.id}
              to={classHref(cls.id)}
              className={cn(
                'flex items-center gap-2 rounded-lg px-3 py-2.5 text-[14px] text-sidebar-foreground/80 transition-colors',
                'hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground',
              )}
            >
              <BookOpen className='h-4 w-4 shrink-0 opacity-80' />
              <span className='min-w-0 truncate font-medium'>{cls.name}</span>
            </Link>
          ))
        )}
      </nav>
    </div>
  )
}
